import dayjs from 'dayjs';
import { isArray, isPlainObject, some } from 'lodash-es';
import { dateUtils } from 'shared';

/**
 * Converts Unix timestamp to yyyy-mm-dd format
 * Used to convert search dates from bloomreach storage format to accelera format
 */
export const unixToDateString = (unix: number): string => {
  return dayjs.unix(unix).format('YYYY-MM-DD');
};

/**
 * Converts ISO datetime string to local time format (YYYY-MM-DD HH:mm:ss)
 * Removes timezone to keep airport local time
 */
const isoToLocalTimeString = (iso: string): string => {
  return dateUtils.format(dateUtils.removeTimezone(iso), 'YYYY-MM-DD HH:mm:ss');
};

/**
 * Converts ISO datetime string to Unix timestamp in point data
 */
const convertPointToUnix = (
  point: Record<string, unknown>,
): Record<string, unknown> => {
  if (typeof point.at === 'string') {
    return { ...point, at: dateUtils.toUnix(point.at) };
  }
  return point;
};

/**
 * Converts ISO datetime strings to Unix timestamps in segment data
 */
const convertSegmentToUnix = (
  segment: Record<string, unknown>,
): Record<string, unknown> => {
  const result = { ...segment };
  if (segment.dep && typeof segment.dep === 'object') {
    result.dep = convertPointToUnix(segment.dep as Record<string, unknown>);
  }
  if (segment.arr && typeof segment.arr === 'object') {
    result.arr = convertPointToUnix(segment.arr as Record<string, unknown>);
  }
  return result;
};

/**
 * Checks if value contains nested objects or arrays
 */
const hasNestedObjects = (value: unknown): boolean => {
  if (isArray(value) || isPlainObject(value)) {
    return some(value, item => isPlainObject(item) || isArray(item));
  }
  return false;
};

/**
 * Converts all arrays and objects with nested structures to JSON strings
 * All arrays are stringified regardless of their content
 */
export const stringifyAllObjectsAndArrays = (
  data: Record<string, unknown>,
): void => {
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (isArray(value) || hasNestedObjects(value)) {
      data[key] = JSON.stringify(value);
    }
  }
};

export const convertToAcceleraFormat = (
  data: Record<string, unknown>,
): void => {
  // Convert search dates from Unix to yyyy-mm-dd
  if (typeof data.date_from === 'number') {
    data.date_from = unixToDateString(data.date_from);
  }
  if (typeof data.date_to === 'number') {
    data.date_to = unixToDateString(data.date_to);
  }

  // Convert top-level datetime fields from ISO to local time
  if (typeof data.from_dep === 'string' && data.from_dep.includes('T')) {
    data.from_dep = isoToLocalTimeString(data.from_dep);
  }
  if (typeof data.from_arr === 'string' && data.from_arr.includes('T')) {
    data.from_arr = isoToLocalTimeString(data.from_arr);
  }
  if (typeof data.to_dep === 'string' && data.to_dep.includes('T')) {
    data.to_dep = isoToLocalTimeString(data.to_dep);
  }
  if (typeof data.to_arr === 'string' && data.to_arr.includes('T')) {
    data.to_arr = isoToLocalTimeString(data.to_arr);
  }

  // Convert segments to use Unix timestamps for at fields
  if (Array.isArray(data.segments_data_from)) {
    data.segments_data_from = data.segments_data_from.map(segment =>
      convertSegmentToUnix(segment as Record<string, unknown>),
    );
  }
  if (Array.isArray(data.segments_data_to)) {
    data.segments_data_to = data.segments_data_to.map(segment =>
      convertSegmentToUnix(segment as Record<string, unknown>),
    );
  }
};
